import { useState } from 'react';
import {
  listIntegrations,
  saveIntegration,
  syncIntegration,
  testIntegration,
  type Integration,
} from './integrations-api';
import { useData } from './useData';

function IntegrationCard({ it, onReload }: { it: Integration; onReload: () => void }) {
  const [enabled, setEnabled] = useState(it.enabled);
  const [cfg, setCfg] = useState<Record<string, string>>(it.config);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const set = (name: string, v: string) => setCfg((p) => ({ ...p, [name]: v }));

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      // не отправляем маскированные секреты без изменений
      const out: Record<string, string> = {};
      for (const f of it.fields) {
        const v = cfg[f.name] ?? '';
        if (f.type === 'password' && v === '••••••••') continue;
        out[f.name] = v;
      }
      await saveIntegration(it.key, enabled, out);
      setMsg('Сохранено');
      onReload();
    } finally {
      setBusy(false);
    }
  }
  async function test() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await testIntegration(it.key);
      setMsg((r.ok ? '✓ ' : '✗ ') + r.message);
    } catch {
      setMsg('Ошибка проверки');
    } finally {
      setBusy(false);
    }
  }
  async function sync() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await syncIntegration(it.key);
      setMsg(r.message);
      onReload();
    } catch {
      setMsg('Ошибка');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="glass pad" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div className="grow">
          <div style={{ fontWeight: 800, fontSize: 15 }}>{it.title}</div>
          <div className="muted small">{it.desc}</div>
        </div>
        <label className="switch">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{enabled ? 'вкл' : 'выкл'}</span>
        </label>
      </div>

      {it.key === 'site' && (
        <div className="bank-note">
          <div className="muted small">Webhook для формы на сайте (метод POST, JSON):</div>
          <code className="wh">
            {it.webhook_url}?token={it.secret}
          </code>
          <div className="muted small" style={{ marginTop: 6 }}>
            Тело:{' '}
            <code>{'{ "client": "...", "region": "...", "phone": "...", "note": "..." }'}</code> →
            создаётся сделка в этапе «Квалификация».
          </div>
        </div>
      )}

      {it.fields.length > 0 && (
        <div className="fields">
          {it.fields.map((f) => (
            <label key={f.name} className="field">
              <span className="muted small">{f.label}</span>
              {f.type === 'select' ? (
                <select value={cfg[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)}>
                  <option value="">— выбрать —</option>
                  {f.options?.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type === 'password' ? 'password' : 'text'}
                  value={cfg[f.name] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              )}
            </label>
          ))}
        </div>
      )}

      <div
        style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <button className="btn sm" disabled={busy} onClick={save}>
          Сохранить
        </button>
        {it.canTest && (
          <button className="btn ghost sm" disabled={busy} onClick={test}>
            Проверить подключение
          </button>
        )}
        {it.canSync && (
          <button className="btn ghost sm" disabled={busy} onClick={sync}>
            {it.syncLabel}
          </button>
        )}
        {msg && (
          <span className="small" style={{ color: 'var(--tx2)' }}>
            {msg}
          </span>
        )}
      </div>
      {it.status && (
        <div className="muted small" style={{ marginTop: 8 }}>
          Последний статус: {it.status}
        </div>
      )}
    </div>
  );
}

// Панель интеграций для встраивания во вкладку «Настройки».
export function IntegrationsPanel() {
  const { data, refresh } = useData('integrations', listIntegrations);
  const list = data?.integrations ?? null;

  if (!list) return <p className="muted">Загрузка…</p>;

  return (
    <>
      <p className="muted small" style={{ marginBottom: 12 }}>
        Подключаются самостоятельно — впишите ключи и включите.
      </p>
      {list.map((it) => (
        <IntegrationCard key={it.key} it={it} onReload={refresh} />
      ))}
    </>
  );
}
